import { cn } from "@vc/lib/utils"
import { Radio, Square, Wifi } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

export function BroadcastIndicator({
  isLive,
  platformLabel,
  durationSeconds,
  connection,
  onStop,
  className,
}: {
  isLive: boolean
  platformLabel?: string
  durationSeconds: number
  /**
   * Whether the last heartbeat reached the mixer.
   *
   * Replaces a viewer count that was `previous + Math.floor(Math.random() * 3)`.
   * A real audience figure has to come from the platform's own API, which is a
   * separate integration; showing an invented one next to a live badge is worse
   * than showing nothing. This is something we actually know.
   */
  connection?: 'live' | 'reconnecting'
  onStop: () => void
  className?: string
}) {
  const t = useTranslation();
  if (!isLive) return null

  const fmt = (s: number) => {
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    return h > 0
      ? `${h}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`
      : `${m}:${sec.toString().padStart(2, "0")}`
  }

  return (
    <div
      className={cn(
        "fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2 rounded-full shadow-lg",
        "bg-rose-600/90 backdrop-blur-sm ring-1 ring-white/15",
        className,
      )}
    >
      <div className="relative flex items-center gap-2">
        <div className="relative">
          <Radio className="h-4 w-4 text-white" />
          <span className="absolute inset-0 animate-ping rounded-full bg-card/40" />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-[11px] font-bold uppercase tracking-wide text-white">
            {t("overlays.live")}{platformLabel ? ` · ${platformLabel}` : ""}
          </span>
          <span className="text-[10px] font-mono text-white/80 tabular-nums">{fmt(durationSeconds)}</span>
        </div>
      </div>

      {connection === 'reconnecting' && (
        <>
          <div className="h-6 w-px bg-card/25" />
          <div className="flex items-center gap-1.5" title="Reconnecting to the streaming server">
            <Wifi className="h-3.5 w-3.5 text-white/80" />
            <span className="text-[11px] font-semibold text-white">Reconnecting</span>
          </div>
        </>
      )}

      <button
        type="button"
        onClick={onStop}
        className="ml-2 inline-flex items-center gap-1 rounded-full bg-card/15 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-card/20 transition"
      >
        <Square className="h-3.5 w-3.5 fill-white" />
        {t("overlays.stop")}
      </button>
    </div>
  )
}
